import { ReactNode } from 'react';

import { Bars4Icon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

interface DragDropFieldsProps {
  items: any[];
  onReorder: (newItems: any[]) => void;
  renderItem: (item: any, index: number) => ReactNode;
  droppableId?: string;
}

export default function DragDropFields({
  items,
  onReorder,
  renderItem,
  droppableId = 'droppable',
}: DragDropFieldsProps) {
  const onDragEnd = (result) => {
    if (!result.destination) return;

    const newItems = [...items];
    const [reorderedItem] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, reorderedItem);

    onReorder(newItems);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId={droppableId}>
        {(provided) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            className="relative"
          >
            {items.map((item, index) => (
              <Draggable
                key={index}
                draggableId={`${droppableId}-${index}`}
                index={index}
                isDragDisabled={items.length === 1}
              >
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    className="group relative mb-4"
                  >
                    <div className="flex flex-col gap-4">
                      <div className="flex items-start gap-4">
                        <div
                          {...provided.dragHandleProps}
                          className={clsx(
                            'flex h-full cursor-grab items-start',
                            'lg:absolute lg:left-0 lg:-translate-x-full lg:p-2',
                            items.length === 1 &&
                              'cursor-not-allowed opacity-50',
                            items.length > 1 &&
                              'lg:opacity-50 lg:transition-opacity lg:group-hover:opacity-100',
                          )}
                        >
                          <Bars4Icon className="mt-14 h-4 sm:mt-12" />
                        </div>
                        {renderItem(item, index)}
                      </div>
                    </div>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}
